import { __decorate, __metadata } from "tslib";
import { addDays, addHours, isBefore, isEqual, subDays } from 'date-fns';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import BaseVue from '~/nasa_ui/base/BaseVue';
import { OUTAGES_POLLING_RATE } from '~/nasa_ui/constants';
import CurrentUserMixin, { logout } from '~/nasa_ui/mixins/CurrentUserMixin';
import { AlertType } from '~/nasa_ui/types';
import { getOutages } from '~/nasa_ui/utils';
import { dateTimeDisplay } from '~/nasa_ui/utils/dates';
import { markdownToHtml } from '~/nasa_ui/utils/helpers/markdownToHtml';
let TheOutageCallout = class TheOutageCallout extends Mixins(BaseVue, CurrentUserMixin) {
    AlertType = AlertType;
    manuallyClosedOutageAlert = false;
    outages = [];
    outageTimerId = null;
    pollingTimerId = null;
    secondsUntilOutage = null;
    get isNextOutageWithinThreeHours() {
        if (!this.nextUpcomingOutage) {
            return false;
        }
        const now = new Date();
        const threeHoursFromNow = addHours(now, 3);
        const outageStart = new Date(this.nextUpcomingOutage.activeStartDateTime);
        return isBefore(outageStart, threeHoursFromNow);
    }
    get isOutageInProgress() {
        const secondsUntilOutage = this.secondsUntilOutage || 0;
        return Boolean(secondsUntilOutage <= 0 && this.nextUpcomingOutage);
    }
    get nextUpcomingOutage() {
        if (this.outages.length === 0) {
            return null;
        }
        const sortedOutages = [...this.outages];
        sortedOutages.sort((a, b) => {
            const startDateA = new Date(a.activeStartDateTime);
            const startDateB = new Date(b.activeStartDateTime);
            if (isEqual(startDateA, startDateB)) {
                return 0;
            }
            return isBefore(startDateA, startDateB) ? -1 : 1;
        });
        return sortedOutages[0];
    }
    get nextUpcomingOutageEnd() {
        return dateTimeDisplay(this.nextUpcomingOutage?.activeEndDateTime);
    }
    get nextUpcomingOutageStart() {
        return dateTimeDisplay(this.nextUpcomingOutage?.activeStartDateTime);
    }
    get nextUpcomingOutageWarningText() {
        return markdownToHtml(this.nextUpcomingOutage?.userFacingMessage);
    }
    get shouldShowCountdown() {
        return this.isNextOutageWithinThreeHours && !this.isOutageInProgress;
    }
    get shouldShowOutageBanner() {
        if (this.manuallyClosedOutageAlert) {
            return false;
        }
        if (this.isNextOutageWithinThreeHours) {
            return true;
        }
        if (this.nextUpcomingOutage) {
            return !localStorage.getItem(`hasUserSeenOutageError${this.nextUpcomingOutage.id}`);
        }
        else {
            return false;
        }
    }
    get outageAlertState() {
        if (this.isOutageInProgress) {
            return AlertType.ERROR;
        }
        else if (this.isNextOutageWithinThreeHours) {
            return AlertType.WARNING;
        }
        else if (this.shouldShowOutageBanner) {
            return AlertType.INFO;
        }
        else
            return null;
    }
    get timeUntilNextUpcomingOutageStarts() {
        const formatSecondsToHHMMSS = (seconds) => {
            const date = new Date(0);
            date.setSeconds(seconds); // specify value for SECONDS here
            return date.toISOString().slice(11, 8);
        };
        return formatSecondsToHHMMSS(this.secondsUntilOutage ?? 0);
    }
    created() {
        this.getOutages();
        // Set Polling for Outages
        this.pollingTimerId = setInterval(() => {
            this.getOutages();
        }, OUTAGES_POLLING_RATE);
    }
    beforeDestroy() {
        if (this.outageTimerId) {
            clearInterval(this.outageTimerId);
        }
        if (this.pollingTimerId) {
            clearInterval(this.pollingTimerId);
        }
    }
    closeAlert(e) {
        localStorage.setItem(`hasUserSeenOutageError${this.nextUpcomingOutage?.id}`, 'true');
        this.manuallyClosedOutageAlert = true;
    }
    onChangeOfNextUpcomingOutage(outage) {
        if (!outage) {
            return;
        }
        this.manuallyClosedOutageAlert = false;
        if (this.isNextOutageWithinThreeHours && !this.outageTimerId) {
            this.startOutageCountdown();
        }
        else {
            this.calculateSecondsUntilOutage();
        }
    }
    calculateSecondsUntilOutage() {
        if (this.nextUpcomingOutage?.activeStartDateTime) {
            const outageStart = new Date(this.nextUpcomingOutage?.activeStartDateTime);
            const now = new Date();
            // starting value
            this.secondsUntilOutage = outageStart.getTime() / 1000 - now.getTime() / 1000;
        }
    }
    startOutageCountdown() {
        // function to recalute the number of seconds until the next outage begins
        this.calculateSecondsUntilOutage();
        // if timer is already running, clear it
        if (this.outageTimerId) {
            clearInterval(this.outageTimerId);
        }
        // update the value every second. saving the timer id to the instance so it
        // can be cleared when needed.
        this.outageTimerId = setInterval(() => {
            this.calculateSecondsUntilOutage();
            // when it gets to 0, stop the interval and log the user out
            if ((this.secondsUntilOutage ?? 0) <= 0 && this.outageTimerId) {
                clearInterval(this.outageTimerId);
                if (!this.currentUserIsAdmin && !this.currentUserIsSystem) {
                    logout();
                }
            }
        }, 1000);
    }
    async getOutages() {
        try {
            // starting with the previous day in case outages span over night
            const yesterday = subDays(new Date(), 1);
            const threeDaysFromToday = addDays(yesterday, 4);
            const params = {
                includeDisabled: false,
                includeInactive: true,
                skip: 0,
                take: 100
            };
            const resp = await getOutages(params);
            const outages = resp.data.results?.filter((outage) => {
                return (outage.activeStartDateTime &&
                    outage.activeStartDateTime > yesterday.toISOString() &&
                    outage.activeStartDateTime < threeDaysFromToday.toISOString() &&
                    outage.activeEndDateTime &&
                    outage.activeEndDateTime > new Date().toISOString());
            });
            // this is a little bit hacky but the purpose is to force watchers to
            // fire again--even if the outages haven't changed
            this.outages = [];
            this.outages = outages;
        }
        catch (err) {
            this.$errorUtility({ err });
        }
    }
};
__decorate([
    Watch('nextUpcomingOutage', { immediate: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], TheOutageCallout.prototype, "onChangeOfNextUpcomingOutage", null);
TheOutageCallout = __decorate([
    Component
], TheOutageCallout);
export default TheOutageCallout;
